import { WithTheme } from '@pixee/design-system'
import * as Sentry from '@sentry/react'
import { setUseWhatChange } from '@simbathesailor/use-what-changed'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import type { DefaultError } from '@tanstack/react-query'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7'
import { useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router'
import { ErrorBoundary } from './components/error-boundaries/error-boundary'
import { ToastsOverlay } from './components/toasts-overlay'
import { ToastServiceProvider } from './services/toast-service'
import { SystemAlertListener } from './services/toast-service/system-alert-listener'
import { isAuthError, isExpectedError, isForbiddenError } from './utils/error-utils'
import { useEnvironmentDataEffects } from './utils/hooks/environment-data-effects'

const APP_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined
if (import.meta.env.PROD) {
  console.log(`✨ User Platform v${APP_VERSION} ✨`)
}

// This allows us to use the `useWhatChanged` hook for debugging dependency arrays.
setUseWhatChange(process.env.NODE_ENV === 'development')

export function Root({
  NuqsAdapterComponent = NuqsAdapter,
}: {
  NuqsAdapterComponent?: React.ComponentType<{ children: React.ReactNode }>
}) {
  const queryClient = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 0,
            gcTime: 300_000,
            throwOnError: (error: DefaultError) => {
              if (!isExpectedError(error)) {
                Sentry.captureException(error)
              }

              return isAuthError(error)
            },
            retry: (failureCount, error: DefaultError) => {
              if (isForbiddenError(error) || isAuthError(error)) {
                return false
              }

              return failureCount < 2
            },
            queryKeyHashFn: queryKey => {
              const keyWithVersion = [...queryKey, APP_VERSION]
              return JSON.stringify(keyWithVersion)
            },
          },
        },
        queryCache: new QueryCache(),
        mutationCache: new MutationCache(),
      })
  )[0]

  const [persister] = useState(() =>
    createAsyncStoragePersister({
      storage: window.localStorage,
    })
  )

  const location = useLocation()

  useEnvironmentDataEffects()

  // Disable persistence in test environment to prevent cache contamination between tests
  // useMemo prevents QueryProvider from being recreated on every render, which would cause
  // React to unmount/remount the entire tree and cancel pending mutations
  const QueryProvider = useMemo(
    () =>
      import.meta.env.MODE === 'test'
        ? ({ children }: { children: React.ReactNode }) => (
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
          )
        : ({ children }: { children: React.ReactNode }) => (
            <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
              {children}
            </PersistQueryClientProvider>
          ),
    [queryClient, persister]
  )

  const content = (
    <QueryProvider>
      <ToastServiceProvider>
        {/* The bundler will exclude this except when `process.env.NODE_ENV === 'development'` */}
        <ReactQueryDevtools />

        <WithTheme>
          <ToastsOverlay />
          <SystemAlertListener />
          <NuqsAdapterComponent>
            <Outlet />
          </NuqsAdapterComponent>
        </WithTheme>
      </ToastServiceProvider>
    </QueryProvider>
  )

  if (location.pathname === '/login') {
    return content
  }

  return <ErrorBoundary fallbackTitle="An error occurred">{content}</ErrorBoundary>
}
